/**
 * The topbar's stock-alert entries (design/ux/01_foundations.md §6.2;
 * cross-module §3.7, 02_DESIGN §3.3; XD-02; WP-18).
 *
 *  · « 14 alertes stock » → `/stock?vue=alertes`: Stock & lots filtered to the
 *    products whose stock is at or below their alert threshold, the filter
 *    visible there; the count is the number of rows that view lists (same
 *    source, stores/stockAlertStore.js).
 *  · « 3 lots périmés » → `/stock?vue=perimes`: a separate entry, never summed
 *    with products.
 * Real links (Ctrl+click opens a tab), 13 px minimum, word + icon + tone.
 * Pharmacy sessions only (an ADMIN_PHARMAKON has no pharmacy).
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui/primitives";
import { useAuthStore } from "@/stores/authStore.js";
import { useStockAlertStore } from "@/stores/stockAlertStore.js";
import { STOCK_VIEWS, stockViewPath } from "@/config/stock-views.config.js";
import { alertesStockLabel, lotsPerimesLabel } from "@/lib/stock-alerts.js";
import { concerneLesCompteurs } from "@/lib/stock-lots.js";
import { ecouterSujet } from "@/services/sujetsTempsReel.js";
import { INVALIDATION_STOCK_UI } from "@/config/ui.config.js";

/** The links for a summary (presentational; the connected component below feeds it). */
export const StockAlertLinks: React.FC<{ summary: { alertesStock: number; lotsPerimes: number } | null }> = ({
  summary,
}) => {
  if (!summary) return null;
  const { alertesStock, lotsPerimes } = summary;
  if (alertesStock === 0 && lotsPerimes === 0) return null;
  return (
    <div className="pk-topbar-alerts">
      {alertesStock > 0 && (
        <Link
          to={stockViewPath(STOCK_VIEWS.ALERTES)}
          className="pk-topbar-alert pk-topbar-alert--warning"
          aria-label={`${alertesStockLabel(alertesStock)} — ouvrir Stock & lots filtré`}
        >
          <Icon name="alert-triangle" size={16} />
          <span className="pk-topbar-alert-count">{alertesStock}</span>
          <span className="pk-topbar-alert-text">{alertesStock > 1 ? "alertes stock" : "alerte stock"}</span>
        </Link>
      )}
      {lotsPerimes > 0 && (
        <Link
          to={stockViewPath(STOCK_VIEWS.PERIMES)}
          className="pk-topbar-alert pk-topbar-alert--danger"
          aria-label={`${lotsPerimesLabel(lotsPerimes)} — ouvrir Stock & lots filtré`}
        >
          <Icon name="calendar-x" size={16} />
          <span className="pk-topbar-alert-count">{lotsPerimes}</span>
          <span className="pk-topbar-alert-text">{lotsPerimes > 1 ? "lots périmés" : "lot périmé"}</span>
        </Link>
      )}
    </div>
  );
};

export const StockAlertEntries: React.FC = () => {
  const pharmacieId: string | null = useAuthStore((s: any) => s.user?.pharmacie_id ?? null);
  const summary = useStockAlertStore((s: any) => (s.pharmacieId === pharmacieId ? s.summary : null));
  const ensure = useStockAlertStore((s: any) => s.ensure);

  const refresh = useStockAlertStore((s: any) => s.refresh);

  React.useEffect(() => {
    if (!pharmacieId) return undefined;
    ensure(pharmacieId);
    // Re-read a stale value when the user comes back to the window — never a timer.
    const onFocus = () => ensure(pharmacieId);
    window.addEventListener("focus", onFocus);
    // X XWP-15 (XA-17) — a stock write elsewhere names `stock:alertes` / `stock:lots` on topic
    // `stock`: the counts are re-read then (the server coalesces a burst into one event).
    const desabonner = ecouterSujet(INVALIDATION_STOCK_UI.sujet, (evt: any) => {
      if (evt?.resync || (evt?.sujet === INVALIDATION_STOCK_UI.sujet && concerneLesCompteurs(evt?.data?.keys))) refresh(pharmacieId, { apresEcriture: true });
    });
    return () => {
      window.removeEventListener("focus", onFocus);
      desabonner();
    };
  }, [pharmacieId, ensure, refresh]);

  if (!pharmacieId) return null;
  return <StockAlertLinks summary={summary} />;
};

export default StockAlertEntries;
